import { useRef, useState, type PointerEvent } from 'react'
import { fmtDate, type Kind } from '../format'

// The design's MChart (mobile: drag to scrub, price tooltip) and PriceChart
// (desktop: price gridlines, date axis, hover) on real series. Sessions are
// evenly spaced, so x is the point's index rather than its date.

export interface Marker {
  index: number
  kind: Kind
  label?: string
}

interface ChartProps {
  values: number[]
  // ISO timestamps, one per value, for the date axis and tooltip.
  dates?: string[]
  markers?: Marker[]
  height: number
  variant: 'mobile' | 'desktop' | 'mini'
  formatValue?: (v: number) => string
  // Line colour; defaults to the series' own direction.
  tone?: 'up' | 'down' | 'flat'
  // A second, muted dashed series on the same scale (e.g. a benchmark), one
  // value per point; null leaves a gap.
  compare?: (number | null)[]
  compareLabel?: string
}

const PAD = {
  mobile: { t: 20, r: 14, b: 22, l: 14 },
  desktop: { t: 18, r: 20, b: 24, l: 52 },
  mini: { t: 6, r: 2, b: 6, l: 2 },
}
const WIDTH = { mobile: 360, desktop: 800, mini: 360 }

const markerColor = (k: Kind) => (k === 'down' ? 'var(--down)' : k === 'hold' ? 'var(--ink-3)' : 'var(--accent)')

export const fmtAxisPrice = (v: number) =>
  `$${v.toLocaleString('en-US', { maximumFractionDigits: v >= 100 ? 0 : 2, minimumFractionDigits: v >= 100 ? 0 : 2 })}`

export function Chart({ values, dates, markers = [], height, variant, formatValue = fmtAxisPrice, tone, compare, compareLabel }: ChartProps) {
  const [hover, setHover] = useState<number | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const W = WIDTH[variant]
  const H = height
  const pad = PAD[variant]
  const cw = W - pad.l - pad.r
  const ch = H - pad.t - pad.b
  const interactive = variant !== 'mini'

  if (values.length < 2) {
    return (
      <div className="chart-empty" style={{ height }}>
        {variant === 'mini' ? null : 'Not enough price history yet'}
      </div>
    )
  }

  const cmp = compare?.slice(0, values.length)
  const scale = [...values, ...(cmp ?? []).filter((v): v is number => v != null)]
  const min = Math.min(...scale)
  const max = Math.max(...scale)
  const spread = max - min || Math.abs(max) * 0.02 || 1
  const lo = min - spread * 0.08
  const hi = max + spread * 0.08
  const x = (i: number) => pad.l + (i / (values.length - 1)) * cw
  const y = (v: number) => pad.t + (1 - (v - lo) / (hi - lo)) * ch
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const cd = cmp ? comparePath(cmp, x, y) : ''

  const t = tone ?? (values[values.length - 1] > values[0] ? 'up' : values[values.length - 1] < values[0] ? 'down' : 'flat')
  const color = t === 'up' ? 'var(--up)' : t === 'down' ? 'var(--down)' : 'var(--ink-2)'

  const move = (e: PointerEvent<HTMLDivElement>) => {
    if (!interactive || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    const rx = ((e.clientX - r.left) / r.width) * W
    const i = Math.round(((rx - pad.l) / cw) * (values.length - 1))
    setHover(Math.max(0, Math.min(values.length - 1, i)))
  }

  const grid = variant === 'desktop' ? [0.05, 0.35, 0.65, 0.95] : variant === 'mobile' ? [0.1, 0.5, 0.9] : []
  const dateTicks =
    dates && variant !== 'mini'
      ? (variant === 'desktop' ? [0, 0.2, 0.4, 0.6, 0.8, 1] : [0, 0.5, 1]).map((f) => Math.round(f * (values.length - 1)))
      : []

  return (
    <div
      ref={ref}
      className="chart"
      style={{ position: 'relative', touchAction: 'pan-y' }}
      onPointerMove={move}
      onPointerDown={move}
      onPointerLeave={() => setHover(null)}
      data-testid={`chart-${variant}`}
    >
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }} role="img" aria-label="Price chart">
        {grid.map((g) => (
          <g key={g}>
            <line x1={pad.l} x2={W - pad.r} y1={pad.t + g * ch} y2={pad.t + g * ch} stroke="var(--border)" />
            {variant === 'desktop' && (
              <text x={pad.l - 8} y={pad.t + g * ch + 4} fontSize="11" fill="var(--ink-3)" textAnchor="end" fontFamily="JetBrains Mono, monospace">
                {formatValue(hi - g * (hi - lo))}
              </text>
            )}
          </g>
        ))}
        {dateTicks.map((i, n) => (
          <text
            key={n}
            x={x(i)}
            y={H - 5}
            fontSize={variant === 'desktop' ? 11 : 10.5}
            fill="var(--ink-3)"
            textAnchor={variant === 'mobile' ? (n === 0 ? 'start' : n === dateTicks.length - 1 ? 'end' : 'middle') : 'middle'}
          >
            {fmtDate(dates![i])}
          </text>
        ))}
        <path d={`${d} L${x(values.length - 1)},${pad.t + ch} L${x(0)},${pad.t + ch} Z`} fill={color} opacity="0.08" />
        {cd && (
          <path d={cd} fill="none" stroke="var(--ink-3)" strokeWidth={1.5} strokeDasharray="4 3" strokeLinecap="round" strokeLinejoin="round" data-testid="chart-compare" />
        )}
        <path d={d} fill="none" stroke={color} strokeWidth={variant === 'mini' ? 1.6 : 2} strokeLinecap="round" strokeLinejoin="round" />
        {markers
          .filter((m) => m.index >= 0 && m.index < values.length)
          .map((m, i) => {
            const mx = x(m.index)
            const my = y(values[m.index])
            const mc = markerColor(m.kind)
            const lw = variant === 'desktop' ? 40 : 30
            return (
              <g key={i} data-testid="chart-marker">
                <line x1={mx} x2={mx} y1={pad.t} y2={pad.t + ch} stroke={mc} strokeDasharray="3 3" opacity="0.6" />
                <circle cx={mx} cy={my} r={variant === 'mini' ? 4 : 6} fill="var(--surface)" stroke={mc} strokeWidth="2.2" />
                {m.label && variant !== 'mini' && (
                  <g>
                    <rect x={Math.min(Math.max(mx - lw / 2, 0), W - lw)} y={1} width={lw} height="15" rx="4" fill={mc} />
                    <text x={Math.min(Math.max(mx, lw / 2), W - lw / 2)} y={11.5} fontSize="9" fill="var(--surface)" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontWeight="600">
                      {m.label}
                    </text>
                  </g>
                )}
              </g>
            )
          })}
        {hover !== null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={pad.t + ch} stroke="var(--ink-3)" strokeDasharray="2 3" />
            <circle cx={x(hover)} cy={y(values[hover])} r="4.5" fill={color} stroke="var(--surface)" strokeWidth="2" />
          </g>
        )}
      </svg>
      {hover !== null && (
        <div className="m-tip" style={{ left: `${(x(hover) / W) * 100}%` }} data-testid="chart-tooltip">
          {formatValue(values[hover])}
          {cmp?.[hover] != null ? ` · ${compareLabel ?? 'vs'} ${formatValue(cmp[hover]!)}` : ''}
          {dates ? ` · ${fmtDate(dates[hover])}` : ''}
        </div>
      )}
    </div>
  )
}

// SVG path for a series with gaps: a new subpath after each null, and a lone
// point between gaps is dropped (it has no line to draw).
export function comparePath(values: (number | null)[], x: (i: number) => number, y: (v: number) => number): string {
  const parts: string[] = []
  let run: string[] = []
  const flush = () => {
    if (run.length > 1) parts.push(run.join(' '))
    run = []
  }
  values.forEach((v, i) => {
    if (v == null) return flush()
    run.push(`${run.length ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
  })
  flush()
  return parts.join(' ')
}

// Index of the first session at or after `iso` (a recommendation's entry), or
// -1 when it falls outside the series.
export function indexAtOrAfter(dates: string[], iso: string | null): number {
  if (!iso) return -1
  const t = new Date(iso).getTime()
  return dates.findIndex((d) => new Date(d).getTime() >= t)
}

export type Timeframe = '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL'
export const TIMEFRAMES: Timeframe[] = ['1W', '1M', '3M', '6M', '1Y', 'ALL']
const DAYS: Record<Timeframe, number> = { '1W': 7, '1M': 31, '3M': 92, '6M': 183, '1Y': 366, ALL: Infinity }

// The series cut to a timeframe, counted back from its newest point.
export function sliceTimeframe<T extends { ts: string }>(points: T[], tf: Timeframe): T[] {
  if (tf === 'ALL' || points.length === 0) return points
  const end = new Date(points[points.length - 1].ts).getTime()
  const from = end - DAYS[tf] * 86400000
  return points.filter((p) => new Date(p.ts).getTime() >= from)
}

export function TimeframeTabs({ value, onChange, variant }: { value: Timeframe; onChange: (t: Timeframe) => void; variant: 'mobile' | 'desktop' }) {
  return (
    <div className={variant === 'mobile' ? 'm-tf' : 'tf'} role="group" aria-label="Timeframe">
      {TIMEFRAMES.map((o) => (
        <button key={o} className={value === o ? 'active' : ''} onClick={() => onChange(o)} aria-pressed={value === o}>
          {o}
        </button>
      ))}
    </div>
  )
}
